/**
 * Employment Type / Status / Role filters for the hiring capacity timeline (David, Sep 2026).
 *
 * The org tree is fetched once and cached (`InternalPlaygroundHiringTimelineView`'s `staleTime`),
 * so filtering runs entirely client-side rather than re-querying the proxy per filter change —
 * `filterOrgTree` below recurses the same `OrgNode`/`OrgLeaf` shape `OrgChartTable` already renders
 * and recomputes each surviving node's `fteByMonth` from scratch, using `ownFteByMonth` (this
 * node's own contribution, excluding its rollup) so the recompute doesn't need the raw per-employee
 * figures a second time.
 *
 * All three filters share one "empty selection ⇒ no restriction" convention — the same pairing
 * `TicketManagementView.tsx` already uses for its own checkbox filters (`size === 0 || has(...)`) —
 * so Employment Type/Status default to every box ticked (behaviourally identical to none ticked,
 * but visibly communicates "everything is currently shown, individually adjustable") and Role
 * defaults to no selection (ticking every one of a long role list just to see everything would be
 * poor UX).
 */
import type { EmploymentType, OrgLeaf, OrgNode } from '../../services/internalPlaygroundApi';

export type LifecycleStatus = 'proposed' | 'to_be_hired' | 'onboarding' | 'leaving' | 'active' | 'left';

export const EMPLOYMENT_TYPE_OPTIONS: { value: EmploymentType; label: string }[] = [
  { value: 'intern_ws', label: 'Interns & Working Students' },
  { value: 'permanent', label: 'Permanent Employees' },
];

export const STATUS_OPTIONS: { value: LifecycleStatus; label: string }[] = [
  // Proposed sits ahead of To be hired because it is the earliest point in a role's life — it is
  // not yet demand. Ticked by default like every other bucket; unticking it removes the purple
  // rows AND their FTE, which is how you read the tree as committed-only.
  { value: 'proposed', label: 'Proposed' },
  { value: 'to_be_hired', label: 'To be hired' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'leaving', label: 'Leaving' },
  { value: 'active', label: 'Active' },
  { value: 'left', label: 'Left' },
];

export interface TimelineFilters {
  employmentTypes: Set<EmploymentType>;
  statuses: Set<LifecycleStatus>;
  /** Empty ⇒ any role. Unlike the other two, there is no sensible "tick every role" default. */
  roles: Set<string>;
}

export function defaultTimelineFilters(): TimelineFilters {
  return {
    employmentTypes: new Set(EMPLOYMENT_TYPE_OPTIONS.map((o) => o.value)),
    statuses: new Set(STATUS_OPTIONS.map((o) => o.value)),
    roles: new Set(),
  };
}

/** Today as 'YYYY-MM-DD' — the same convention `OrgChartTable`'s row annotation
 * (`PersonDateNote`) already compares against, kept here so both read "today" identically. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** An empty filter set lets everything through. */
function passesSet<T extends string>(value: T, set: Set<T>): boolean {
  return set.size === 0 || set.has(value);
}

/** Mutually exclusive lifecycle bucket, mirroring `PersonDateNote`'s own precedence (termination
 * wins over hire) so the filter and the on-row annotation can never disagree. `isPlanned` is only
 * ever true for an open/backlog/on-hold `OrgLeaf` (`kind === 'planned'`) — every `OrgNode` this is
 * called for carries a role: a real person, or the CEO Office for the CEO (see `role` on `OrgNode`,
 * which gates whether a node's own status is evaluated at all). */
export function lifecycleStatusOf(
  entity: { hireDate?: string | null; terminationDate?: string | null },
  isPlanned: boolean,
  todayIsoDate: string,
  isProposed = false
): LifecycleStatus {
  if (isProposed) return 'proposed';
  if (isPlanned) return 'to_be_hired';
  if (entity.terminationDate) return entity.terminationDate > todayIsoDate ? 'leaving' : 'left';
  if (entity.hireDate && entity.hireDate > todayIsoDate) return 'onboarding';
  return 'active';
}

/** A leaf (employee, planned or proposed role) against every active filter. */
function leafMatches(leaf: OrgLeaf, filters: TimelineFilters, today: string): boolean {
  return (
    passesSet(leaf.employmentType, filters.employmentTypes) &&
    passesSet(
      lifecycleStatusOf(leaf, leaf.kind === 'planned', today, leaf.kind === 'proposed'),
      filters.statuses
    ) &&
    passesSet(leaf.role, filters.roles)
  );
}

/** A node's own attributes only (never its descendants) — see `role`'s doc on `OrgNode`. Judged
 * by `role`, not `isPerson`: the CEO Office is not a person but its own FTE is the CEO's. */
function nodeSelfMatches(node: OrgNode, filters: TimelineFilters, today: string): boolean {
  if (!node.role || !node.employmentType) return false;
  return (
    passesSet(node.employmentType, filters.employmentTypes) &&
    passesSet(lifecycleStatusOf(node.filterDates ?? node, false, today), filters.statuses) &&
    passesSet(node.role, filters.roles)
  );
}

function sumFteInto(target: Record<string, number>, source: Record<string, number>) {
  for (const [month, fte] of Object.entries(source)) target[month] = (target[month] ?? 0) + fte;
}

/**
 * Recursively filters and prunes the org tree, recomputing `fteByMonth` from surviving descendants
 * only. A node whose own attributes don't match the filters is KEPT (contributing no FTE of its
 * own) as long as at least one descendant still matches — so a filtered manager's row still shows
 * the right total for their filtered team, and the tree's shape (who reports to whom) survives the
 * filter instead of only ever showing a flat list of matching leaves. A node with no surviving
 * descendants that also doesn't match itself is dropped entirely. Returns `null` when nothing in
 * the whole subtree survives.
 */
export function filterOrgTree(node: OrgNode, filters: TimelineFilters, today: string): OrgNode | null {
  const subManagers = node.subManagers
    .map((child) => filterOrgTree(child, filters, today))
    .filter((n): n is OrgNode => n !== null);

  const roleGroups = node.roleGroups
    .map((group) => ({ role: group.role, entries: group.entries.filter((leaf) => leafMatches(leaf, filters, today)) }))
    .filter((group) => group.entries.length > 0);

  const selfMatches = nodeSelfMatches(node, filters, today);
  if (!selfMatches && subManagers.length === 0 && roleGroups.length === 0) return null;

  // Both rollups are recomputed, and a surviving leaf goes to exactly one of them — the same
  // split the proxy makes in `addLeafFte`. Keeping `fteByMonth` committed-only here too is what
  // lets the purple delta survive filtering without the black figure ever absorbing it.
  const fteByMonth: Record<string, number> = selfMatches ? { ...node.ownFteByMonth } : {};
  const proposedFteByMonth: Record<string, number> = {};
  subManagers.forEach((child) => {
    sumFteInto(fteByMonth, child.fteByMonth);
    // `?? {}` because the WIRE FORMAT can genuinely omit this: `deploy-frontend` carries no
    // `needs: deploy-proxy`, so a deploy serves this frontend against the previous Cloud Run
    // revision for a while. It is not reachable today — `child` is always a node this function
    // just rebuilt, never a raw payload one — so this is a guard on the type, not a live fix.
    // Keep it: `sumFteInto` would throw on `Object.entries(undefined)`, and the first reader that
    // walks the tree without recursing through here would hit exactly that.
    sumFteInto(proposedFteByMonth, child.proposedFteByMonth ?? {});
  });
  roleGroups.forEach((group) =>
    group.entries.forEach((leaf) =>
      sumFteInto(leaf.kind === 'proposed' ? proposedFteByMonth : fteByMonth, leaf.fteByMonth)
    )
  );

  return { ...node, subManagers, roleGroups, fteByMonth, proposedFteByMonth };
}

/** [role, count] pairs present anywhere in `node`'s own subtree (itself, every sub-manager, every
 * leaf) — sorted by role. Always computed from the UNFILTERED subtree, so narrowing Employment
 * Type/Status can never make a previously-visible Role option disappear out from under a pending
 * selection. Options are scoped to whichever tab is active, but the Role SELECTION itself is not
 * reset on tab change (filters persist across tabs, David, Sep 2026) — a role carried over from a
 * department that doesn't have it simply matches nothing in this scope, which is correct, not a
 * bug. */
export type FilterOption = [string, number];

function collectRoles(node: OrgNode, counts: Map<string, number>) {
  if (node.isPerson && node.role) counts.set(node.role, (counts.get(node.role) ?? 0) + 1);
  node.subManagers.forEach((child) => collectRoles(child, counts));
  node.roleGroups.forEach((group) => group.entries.forEach((leaf) => counts.set(leaf.role, (counts.get(leaf.role) ?? 0) + 1)));
}

export function rolesInScope(node: OrgNode): FilterOption[] {
  const counts = new Map<string, number>();
  collectRoles(node, counts);
  return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

/** True once any dimension is actually narrowing the tree — not merely "every box happens to be
 * ticked", which behaves identically to none ticked (see the module doc's "empty ⇒ unrestricted"
 * convention) and must not trigger the before/after FTE display below. */
export function isFilteringActive(filters: TimelineFilters): boolean {
  const restricts = (size: number, total: number) => size > 0 && size < total;
  return (
    restricts(filters.employmentTypes.size, EMPLOYMENT_TYPE_OPTIONS.length) ||
    restricts(filters.statuses.size, STATUS_OPTIONS.length) ||
    filters.roles.size > 0
  );
}

/**
 * Every `OrgNode`'s own id → its UNFILTERED `fteByMonth`, so a filtered view can show "N of the
 * unfiltered M" per row. Only nodes are collected, never leaves: a leaf's `fteByMonth` is either
 * shown in full (it passed the filter) or not at all (it didn't) — filtering only ever excludes a
 * leaf outright, it never shrinks one, so there is nothing to compare at that level. A node,
 * by contrast, is a rollup over its descendants, which filtering DOES shrink — see `filterOrgTree`.
 */
export function fteByMonthByNodeId(node: OrgNode, map: Map<string, Record<string, number>> = new Map()) {
  map.set(node.id, node.fteByMonth);
  node.subManagers.forEach((child) => fteByMonthByNodeId(child, map));
  return map;
}
